import type { LeaveRuleDto, LeaveTierDto } from '../api/hr-admin';

/**
 * 연차 자동 계산 — **화면에서 미리 보여 주기 위한 것**이다 (근태 0단계, 2026-08-23).
 *
 * 진실은 서버(`apps/chat-server/src/modules/hr/leave-rules.ts`)에 있고, 저장되는 값도 서버가
 * 계산한 것이다. 여기 계산은 화면 C 의 "칸에 입사일을 넣으면 오른쪽 두 칸이 **바로** 바뀐다"
 * 를 위해서만 쓴다 — 한 글자 칠 때마다 서버에 물으면 표가 덜컹거린다.
 *
 * ★ **규칙은 복제하지 않는다.** tiers·상한·가산은 서버 응답(`rule`)을 그대로 받아 쓴다.
 *   여기 있는 것은 그 규칙을 읽는 방법뿐이라, 회사가 규칙을 바꿔도 이 파일은 그대로다.
 */

/** `YYYY-MM-DD` → UTC 자정. 형식이 아니거나 없는 날짜면 null. */
export function parseDateOnly(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const dt = new Date(Date.UTC(y, mo - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d) return null;
  return dt;
}

/** 근속 개월 — 달을 세고 일자로 보정한다(서버와 같은 규칙). 입사 전이면 0. */
export function tenureMonths(hireDate: Date, asOf: Date): number {
  const y = asOf.getUTCFullYear() - hireDate.getUTCFullYear();
  const m = asOf.getUTCMonth() - hireDate.getUTCMonth();
  let months = y * 12 + m;
  if (asOf.getUTCDate() < hireDate.getUTCDate()) months -= 1;
  return months > 0 ? months : 0;
}

/** "7년 4개월". 1년 미만이면 "4개월", 입사 전이면 "—". */
export function tenureLabel(months: number): string {
  if (months <= 0) return '—';
  const y = Math.floor(months / 12);
  const m = months % 12;
  if (y === 0) return `${m}개월`;
  if (m === 0) return `${y}년`;
  return `${y}년 ${m}개월`;
}

/** 자동 부여 일수 = 입사일 × 규칙. 서버 `computeAnnualDays` 와 같은 해석. */
export function computeAnnualDays(hireDate: Date, asOf: Date, rule: LeaveRuleDto): number {
  const months = tenureMonths(hireDate, asOf);
  if (months <= 0) return 0;

  let applied: LeaveTierDto | undefined;
  for (const tier of [...rule.tiers].sort((a, b) => a.months - b.months)) {
    if (months >= tier.months) applied = tier;
  }
  if (!applied) return 0;

  let days: number;
  if (applied.perMonth !== undefined) {
    days = months * applied.perMonth;
    if (applied.max !== undefined) days = Math.min(days, applied.max);
  } else {
    days = applied.days ?? 0;
    if (applied.everyMonths && applied.plus) {
      days += Math.floor((months - applied.months) / applied.everyMonths) * applied.plus;
    }
  }
  if (months < 12) days += rule.firstYearBonus;
  return Math.max(0, Math.min(days, rule.maxDays));
}

/** 그 해의 기준일 — 올해면 오늘, 지난 해면 12/31, 다음 해면 1/1 (서버와 같다). */
export function asOfForYear(year: number, now: Date = new Date()): Date {
  const thisYear = now.getUTCFullYear();
  if (year === thisYear) return new Date(Date.UTC(thisYear, now.getUTCMonth(), now.getUTCDate()));
  if (year < thisYear) return new Date(Date.UTC(year, 11, 31));
  return new Date(Date.UTC(year, 0, 1));
}

/** 규칙을 사람 말로 — 화면 C·O 의 "연차 부여 규칙" 카드. */
export function describeTiers(rule: LeaveRuleDto): string[] {
  const lines: string[] = [];
  for (const t of [...rule.tiers].sort((a, b) => a.months - b.months)) {
    if (t.perMonth !== undefined) {
      lines.push(`${t.months === 0 ? '1년 미만' : `${Math.floor(t.months / 12)}년 미만`} · 월 ${t.perMonth}일` +
        (t.max !== undefined ? ` (최대 ${t.max})` : ''));
    } else if (t.everyMonths && t.plus) {
      lines.push(`${Math.floor(t.months / 12)}년째부터 ${t.everyMonths / 12}년마다 +${t.plus} (최대 ${rule.maxDays})`);
    } else {
      lines.push(`${Math.floor(t.months / 12)}년 이상 · ${t.days ?? 0}일`);
    }
  }
  return lines;
}
